import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import useShouldShowAds from '../../hooks/useShouldShowAds';
import { pickAffiliateProducts } from '../../utils/pickAffiliateProducts';

/**
 * Quiet native strip of Amazon affiliate products for general pages.
 * Hidden for premium users.
 */
export default function NativeAffiliateBanner({
  placement = 'general',
  productCount = 3,
  className = '',
}) {
  const showAds = useShouldShowAds();
  const products = useMemo(
    () => pickAffiliateProducts(productCount, placement.length),
    [placement, productCount],
  );

  if (!showAds || products.length === 0) return null;

  const trackClick = (product) => {
    posthog.capture('internal_ad_click', {
      placement,
      format: 'native_banner',
      product_id: product.id,
      product_title: product.title,
    });
  };

  return (
    <aside
      className={`border-y border-gray-100 bg-gray-50/80 ${className}`}
      aria-label="Sponsored recommendations"
    >
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Sponsored · Readers also browse
          </p>
          <Link to="/shop" className="text-xs text-violet-900 hover:underline">
            Browse shop
          </Link>
        </div>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {products.map((product) => (
            <li key={`${placement}-${product.id}`}>
              <a
                href={product.affiliateUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => trackClick(product)}
                className="group flex items-center gap-3 rounded-lg p-2 transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-800"
              >
                <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden bg-white">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  ) : null}
                </div>
                <div className="min-w-0 text-left">
                  <p className="line-clamp-2 text-sm font-medium text-gray-900 group-hover:text-violet-900">
                    {product.title}
                  </p>
                  <p className="mt-0.5 text-xs text-violet-900">View on Amazon →</p>
                </div>
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-center text-[11px] text-gray-400">
          As an Amazon Associate, Poppyruz earns from qualifying purchases.
        </p>
      </div>
    </aside>
  );
}
